import type { ChangeEvent, FunctionComponent } from 'react';
import { useState } from 'react';

import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import type { CurrencyInfo } from '../../../store/slices/game/types';
import { BaseInput, type InputProps } from '../baseInput/BaseInput';

export interface CurrencyInputProps extends Omit<InputProps, 'prefix'> {
  currency: string;
  initialValue?: string;
  containerClass?: string;
  currencyInfo?: CurrencyInfo;
  errorMsg?: string;
}

export const CurrencyInput: FunctionComponent<CurrencyInputProps> = ({
  currency,
  initialValue,
  currencyInfo,
  errorMsg,
  ...rest
}) => {
  const [error, setError] = useState('');
  const [value, setValue] = useState(initialValue);

  let placeholder = '0';

  if (currencyInfo && currencyInfo.digitsNumber > 0) {
    placeholder += currencyInfo.decimalSeparator;
    for (let i = 0; i < currencyInfo.digitsNumber; i++) {
      placeholder += 0;
    }
  }

  const validateCurrency = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const validityState = input.validity;

    if (validityState.valueMissing) {
      setError('REQUIRED');
    } else if (validityState.tooLong || validityState.rangeOverflow) {
      setError(errorMsg ?? 'INCORRECT_VALUE');
    } else if (
      validityState.rangeUnderflow ||
      validityState.patternMismatch ||
      validityState.stepMismatch ||
      validityState.tooShort ||
      validityState.typeMismatch ||
      validityState.badInput
    ) {
      setError('INCORRECT_VALUE');
    }
  };

  const resetErrors = (): void => {
    setError('');
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
  };

  return (
    <BaseInput
      prefix={currency}
      type="number"
      step={Math.pow(10, -(currencyInfo?.digitsNumber || 0))}
      min={0}
      onChange={onChange}
      placeholder={placeholder}
      value={value}
      errorMessage={error ? <L10nText tk={error} /> : undefined}
      onInvalid={validateCurrency}
      onFocus={resetErrors}
      {...rest}
    />
  );
};
